// NOTE: NSAI-6112 — the ONLY messages a redirect back from the server may put on
// screen. Every OAuth / integration / calendar callback lands on a client URL
// carrying `success=true` or `error=true` plus a message, and anyone can craft
// such a URL and send it as a link. Rendering its text verbatim let a stranger
// choose the wording of an official-looking toast on the genuine origin
// (reflected content spoofing, CWE-451). A client therefore renders a callback
// message only by looking its CODE up here; everything else — an unknown code,
// or the legacy free-text `message` param — falls back to the generic wording.
//
// The codes are the server i18n keys the callbacks already translate from, so
// the server sends them unchanged as `messageKey`. The `calendars:toasts.*`
// entries are the codes the calendar callbacks were ALREADY sending as
// `messageKey` before this allowlist existed; they stay valid as-is.
//
// `isSuccess` pins each code to its outcome, so `?success=true` cannot paint a
// failure message green (or the reverse) — a mismatch is treated as unknown.

export interface ICallbackMessageEntry {
  key: TCallbackMessageKey;
  isSuccess: boolean;
}

export type TCallbackMessageKey =
  | "integration:callbackMessages.connected"
  | "integration:callbackMessages.connectFailed"
  | "integration:callbackMessages.connectionCancelled"
  | "integration:callbackMessages.invalidToken"
  | "integration:callbackMessages.requestExpired"
  | "integration:callbackMessages.invalidCredentials"
  | "integration:callbackMessages.missingCredentials"
  | "integration:callbackMessages.bamboohrDomainInvalid"
  | "integration:callbackMessages.notConfiguredForOrganization"
  | "integration:callbackMessages.mcpManagedByOrganization"
  | "integration:callbackMessages.mcpAuthHeaderNameRequired"
  | "integration:callbackMessages.mcpTransportUnsupported"
  | "integration:callbackMessages.mcpUnauthorized"
  | "integration:callbackMessages.mcpConnectionTimeout"
  | "integration:callbackMessages.mcpServerError"
  | "calendars:toasts.googleCalendarConnected"
  | "calendars:toasts.googleCalendarAccessDenied"
  | "calendars:toasts.failedToConnectGoogleCalendar"
  | "calendars:toasts.microsoftCalendarConnected"
  | "calendars:toasts.microsoftCalendarAccessDenied"
  | "calendars:toasts.failedToConnectMicrosoftCalendar"
  | "common:callback_message.generic_error"
  | "common:callback_message.generic_success";

export const CALLBACK_MESSAGE_GENERIC_ERROR_KEY =
  "common:callback_message.generic_error" as const;
export const CALLBACK_MESSAGE_GENERIC_SUCCESS_KEY =
  "common:callback_message.generic_success" as const;

const CONNECTED = {
  key: "integration:callbackMessages.connected",
  isSuccess: true,
} as const;
const CONNECT_FAILED = {
  key: "integration:callbackMessages.connectFailed",
  isSuccess: false,
} as const;
const REQUEST_EXPIRED = {
  key: "integration:callbackMessages.requestExpired",
  isSuccess: false,
} as const;
const INVALID_CREDENTIALS = {
  key: "integration:callbackMessages.invalidCredentials",
  isSuccess: false,
} as const;
const MISSING_CREDENTIALS = {
  key: "integration:callbackMessages.missingCredentials",
  isSuccess: false,
} as const;

export const CALLBACK_MESSAGE_CODES: Readonly<
  Record<string, ICallbackMessageEntry>
> = {
  "integration.callback_success": CONNECTED,
  "integration.callback_error": CONNECT_FAILED,
  "integration.failed_to_connect": CONNECT_FAILED,
  "integration.oauth_callback_error": CONNECT_FAILED,
  "integration.invalid_user_info": CONNECT_FAILED,
  "integration.invalid_provider": CONNECT_FAILED,
  "integration.project_not_found": CONNECT_FAILED,
  "integration.oauth_connection_cancelled": {
    key: "integration:callbackMessages.connectionCancelled",
    isSuccess: false,
  },
  "integration.invalid_token": {
    key: "integration:callbackMessages.invalidToken",
    isSuccess: false,
  },
  "integration.invalid_code_or_state": REQUEST_EXPIRED,
  "integration.state_required": REQUEST_EXPIRED,
  "integration.code_required": REQUEST_EXPIRED,
  "integration.user_id_required": REQUEST_EXPIRED,
  "integration.dto.project_id_and_code_required": REQUEST_EXPIRED,
  "integration.dto.project_id_and_provider_required": REQUEST_EXPIRED,
  "integration.dto.project_id_and_code_verifier_required": REQUEST_EXPIRED,
  "integration.callback_invalid_credentials": INVALID_CREDENTIALS,
  "integration.invalid_ews_credentials": INVALID_CREDENTIALS,
  "integration.invalid_api_key": INVALID_CREDENTIALS,
  "integration.invalid_client_id_and_client_secret_and_region":
    INVALID_CREDENTIALS,
  "integration.missing_api_key": MISSING_CREDENTIALS,
  "integration.missing_client_id_and_client_secret_and_region":
    MISSING_CREDENTIALS,
  "integration.bamboohr_company_domain_invalid": {
    key: "integration:callbackMessages.bamboohrDomainInvalid",
    isSuccess: false,
  },
  "integration.atlassian_dc_not_configured": {
    key: "integration:callbackMessages.notConfiguredForOrganization",
    isSuccess: false,
  },
  "integration.invalid_mcp_config": CONNECT_FAILED,
  "integration.invalid_mcp_token": INVALID_CREDENTIALS,
  "integration.mcp_token_required": MISSING_CREDENTIALS,
  "integration.missing_mcp_title_url_and_token": MISSING_CREDENTIALS,
  "integration.mcp_auth_header_name_required": {
    key: "integration:callbackMessages.mcpAuthHeaderNameRequired",
    isSuccess: false,
  },
  "integration.mcp_transport_unsupported": {
    key: "integration:callbackMessages.mcpTransportUnsupported",
    isSuccess: false,
  },
  "integration.mcp_unauthorized": {
    key: "integration:callbackMessages.mcpUnauthorized",
    isSuccess: false,
  },
  "integration.mcp_connection_timeout": {
    key: "integration:callbackMessages.mcpConnectionTimeout",
    isSuccess: false,
  },
  "integration.mcp_server_error": {
    key: "integration:callbackMessages.mcpServerError",
    isSuccess: false,
  },
  "integration.not_able_to_access_mcp_server": CONNECT_FAILED,
  "integration.mcp_managed_by_organization": {
    key: "integration:callbackMessages.mcpManagedByOrganization",
    isSuccess: false,
  },
  "calendars:toasts.googleCalendarConnected": {
    key: "calendars:toasts.googleCalendarConnected",
    isSuccess: true,
  },
  "calendars:toasts.googleCalendarAccessDenied": {
    key: "calendars:toasts.googleCalendarAccessDenied",
    isSuccess: false,
  },
  "calendars:toasts.failedToConnectGoogleCalendar": {
    key: "calendars:toasts.failedToConnectGoogleCalendar",
    isSuccess: false,
  },
  "calendars:toasts.microsoftCalendarConnected": {
    key: "calendars:toasts.microsoftCalendarConnected",
    isSuccess: true,
  },
  "calendars:toasts.microsoftCalendarAccessDenied": {
    key: "calendars:toasts.microsoftCalendarAccessDenied",
    isSuccess: false,
  },
  "calendars:toasts.failedToConnectMicrosoftCalendar": {
    key: "calendars:toasts.failedToConnectMicrosoftCalendar",
    isSuccess: false,
  },
};

// NOTE: NSAI-6112 — the same rule for the `/auth/sso?error=<code>` redirect the
// SAML callback issues on a rejected sign-in. The codes are the server's
// `auth.*` keys; anything else renders the generic SSO failure. The values
// are keys in the `auth` namespace, for the SSO screen's `t` from
// `useTranslation("auth")`.
export type TSsoErrorMessageKey =
  | "sso.errors.callback_failed"
  | "sso.errors.email_mismatch"
  | "sso.errors.invite_only"
  | "sso.errors.registration_disabled"
  | "sso.errors.user_already_exists";

export const SSO_ERROR_GENERIC_KEY = "sso.errors.callback_failed" as const;

export const SSO_ERROR_CODES: Readonly<Record<string, TSsoErrorMessageKey>> = {
  "auth.saml_callback_error": SSO_ERROR_GENERIC_KEY,
  "auth.saml_user_email_mismatch": "sso.errors.email_mismatch",
  "auth.saml_invite_only_rejected": "sso.errors.invite_only",
  "auth.registration_disabled": "sso.errors.registration_disabled",
  "auth.user_with_this_email_already_exists": "sso.errors.user_already_exists",
};

// NOTE: NSAI-6755 — the WARNING codes a SUCCESSFUL SAML callback may attach:
// `?ssoWarning=<code>` on the web `/auth/token` redirect, `&ssoWarning=<code>`
// in the mobile / extension callback fragment. Same rule as the error codes
// above — only a listed code is ever rendered — except that an unknown code
// renders nothing: a warning is not worth guessing at. Mirrors
// SSO_LEGACY_DOMAIN_WARNING_CODE in the server's auth.constants.ts.
export type TSsoWarningMessageKey = "sso.warnings.domain_not_verified";

export const SSO_WARNING_PARAM = "ssoWarning" as const;

export const SSO_WARNING_CODES: Readonly<
  Record<string, TSsoWarningMessageKey>
> = {
  "auth.saml_domain_not_verified": "sso.warnings.domain_not_verified",
};
